/**
 * Single HTTP client. Configure once at app init (token + onUnauthorized).
 */
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosHeaders } from 'axios';
import { ApiResponseLogger } from './ApiResponseLogger';
import { appLogger } from '../utils/logger';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { environment } from '../config/environment';

const DEFAULT_TIMEOUT = 65000;

export interface ApiConfig {
  getToken: () => string | null | undefined;
  onUnauthorized: () => void;
}

/** Payload passed to the API success handler (e.g. for masterData sync). Path is relative, no query. */
export interface ApiSuccessPayload {
  method: string;
  path: string;
  params?: object;
  body?: object;
  response?: unknown;
}

let config: ApiConfig | null = null;
let instance: AxiosInstance | null = null;
let onApiSuccess: ((payload: ApiSuccessPayload) => void) | null = null;

/** Register a single handler called on every successful API response. Used for masterData sync. */
export function setApiSuccessHandler(fn: (payload: ApiSuccessPayload) => void): void {
  onApiSuccess = fn;
}

const PUBLIC_PATHS = [
  'login',
  'register',
  'resend/otp',
  'verify/otp',
  'password/reset',
  'password/update',
  'initial',
  'app_config',
  'banners',
  'pages',
  'areas',
  'subscriptions',
  'menu_items',
  'celebrities',
  'posts',
  'calculate/calories',
  'order',
];

function isPublicPath(url: string): boolean {
  const path = url.replace(environment.API_URL, '').split('?')[0].replace(/^\//, '');
  return PUBLIC_PATHS.some(p => path === p || path.startsWith(p + '/'));
}

/** Optional endpoints — 404/expected failures must not log, report, or block the UI thread. */
const SILENT_NOT_FOUND_PATHS = new Set(['app_config']);

function normalizeApiPath(url: string): string {
  return url.replace(environment.API_URL ?? '', '').split('?')[0].replace(/^\//, '');
}

function isSilentApiFailure(path: string, status?: number): boolean {
  if (status !== 404) {
    return false;
  }
  return SILENT_NOT_FOUND_PATHS.has(path);
}

export function setApiConfig(c: ApiConfig): void {
  config = c;
}

export function getApiClient(): AxiosInstance {
  if (!instance) {
    instance = axios.create({
      baseURL: environment.API_URL,
      timeout: DEFAULT_TIMEOUT,
      headers: new AxiosHeaders({
        'Content-Type': 'application/json',
        accept: 'application/json',
      }),
    });

    instance.interceptors.request.use(
      async (req: InternalAxiosRequestConfig) => {
        // Dev hub can swap API_URL after the singleton was created — always use current env.
        req.baseURL = environment.API_URL;
        if (!req.headers) {
          req.headers = new AxiosHeaders();
        }
        req.headers.set('identifier', environment.IDENTIFIER);
        const token = config?.getToken() ?? (await AsyncStorage.getItem('authToken'));
        if (token) {
          req.headers.set('Authorization', token);
        }
        /** Let the runtime set multipart boundary (RN `FormData`). */
        if (typeof FormData !== 'undefined' && req.data instanceof FormData) {
          req.headers.delete('Content-Type');
        }
        return req;
      },
      e => Promise.reject(e),
    );

    instance.interceptors.response.use(
      (res: AxiosResponse) => {
        try {
          ApiResponseLogger.capture({ url: res.config.url, method: res.config.method }, { status: res.status, data: res.data });
        } catch (_) {}
        try {
          const path = normalizeApiPath(res.config.url || '');
          const { captureDevApiCallHistory } =
            require('../../exDev/apiCallHistory/store') as typeof import('../../exDev/apiCallHistory/store');
          captureDevApiCallHistory({
            method: (res.config.method || 'GET').toUpperCase(),
            path,
            params: res.config.params,
            body: res.config.data,
            response: res.data,
            status: res.status,
          });
        } catch (_) {}
        try {
          const path = normalizeApiPath(res.config.url || '');
          const { captureSubscriptionMenuProbeApiCall } =
            require('../../exDev/probes/subscriptionMenuProbe/logger') as typeof import('../../exDev/probes/subscriptionMenuProbe/logger');
          captureSubscriptionMenuProbeApiCall({
            method: (res.config.method || 'GET').toUpperCase(),
            path,
            params: res.config.params as object | undefined,
            body: res.config.data as object | undefined,
            response: res.data,
            status: res.status,
          });
        } catch (_) {}
        if (__DEV__) {
          try {
            const url = res.config.url || '';
            const path = url.replace(environment.API_URL || '', '').split('?')[0].replace(/^\//, '');
            const method = (res.config.method || 'GET').toUpperCase();
          } catch (_) {}
        }
        const body = res.data;
        const url = res.config.url || '';
        if (body?.code === 401 || (body?.success === false && body?.message === 'Unauthorized.')) {
          if (isPublicPath(url)) {
            return res;
          }
          config?.onUnauthorized();
          return Promise.reject(new Error('Unauthorized'));
        }
        try {
          const path = url.replace(environment.API_URL, '').split('?')[0].replace(/^\//, '');
          onApiSuccess?.({
            method: (res.config.method || 'GET').toUpperCase(),
            path,
            params: res.config.params,
            body: res.config.data as object | undefined,
            response: res.data,
          });
        } catch (_) {}
        return res;
      },
      async err => {
        const url = err.config?.url || '';
        const path = normalizeApiPath(url);
        const status = err.response?.status as number | undefined;
        const silent = isSilentApiFailure(path, status);

        try {
          const { captureDevApiCallHistory } =
            require('../../exDev/apiCallHistory/store') as typeof import('../../exDev/apiCallHistory/store');
          captureDevApiCallHistory({
            method: (err.config?.method || 'GET').toUpperCase(),
            path,
            params: err.config?.params,
            body: err.config?.data,
            response: err.response?.data,
            error: err?.message ?? 'API request failed',
            status,
          });
        } catch (_) {}

        if (err.response?.status === 401 && isPublicPath(url) === false) {
          config?.onUnauthorized();
        }
        if (!silent) {
          appLogger.error(err?.message ?? 'API request failed', {
            url: path || url,
            method: err.config?.method,
            status,
          });
          try {
            const { captureSubscriptionMenuProbeApiCall } =
              require('../../exDev/probes/subscriptionMenuProbe/logger') as typeof import('../../exDev/probes/subscriptionMenuProbe/logger');
            captureSubscriptionMenuProbeApiCall({
              method: (err.config?.method || 'GET').toUpperCase(),
              path,
              params: err.config?.params as object | undefined,
              body: err.config?.data as object | undefined,
              error: err?.message ?? 'API request failed',
              status,
            });
          } catch (_) {}
          void import('../../platform/support/issueReport')
            .then(({ reportApiClientError }) => {
              reportApiClientError(err, {
                url,
                method: err.config?.method,
                params: err.config?.params,
              });
            })
            .catch(() => {});
        }
        return Promise.reject(err);
      },
    );
  }
  return instance;
}

export async function apiGet<T>(endpoint: string, params?: object): Promise<T> {
  const res = await getApiClient().get(endpoint, { params });
  return res.data;
}

export async function apiPost<T>(endpoint: string, data?: object): Promise<T> {
  const res = await getApiClient().post(endpoint, data);
  return res.data;
}

/** POST `multipart/form-data` (e.g. `customer/profile/update`). Do not JSON-stringify `FormData`. */
export async function apiPostFormData<T>(endpoint: string, formData: FormData): Promise<T> {
  const res = await getApiClient().post<T>(endpoint, formData);
  return res.data;
}

export async function apiPut<T>(endpoint: string, data?: object): Promise<T> {
  const res = await getApiClient().put(endpoint, data);
  return res.data;
}

export async function apiDelete<T>(endpoint: string, params?: object): Promise<T> {
  const res = await getApiClient().delete(endpoint, { params });
  return res.data;
}

/** Object-style API surface (same behavior as apiGet / apiPost / …). */
export const ApiClient = {
  get: <T>(endpoint: string, params?: object) => apiGet<T>(endpoint, params),
  post: <T>(endpoint: string, data?: object) => apiPost<T>(endpoint, data),
  postFormData: <T>(endpoint: string, formData: FormData) => apiPostFormData<T>(endpoint, formData),
  put: <T>(endpoint: string, data?: object) => apiPut<T>(endpoint, data),
  delete: <T>(endpoint: string, params?: object) => apiDelete<T>(endpoint, params),
};
